import type { TOC } from '@ember/component/template-only'
import { uniqueId } from '@ember/helper'
import { on } from '@ember/modifier'
import { and } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import InputText from 'teacher-ui-client/components/base/input-text'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { TwoDatePicker } from 'teacher-ui-client/components/dates/two-date-picker'
import { TwoDatePickerInput } from 'teacher-ui-client/components/dates/two-date-picker/input'
import { Tooltip } from 'teacher-ui-client/components/tooltip'

interface Signature {
  Element: HTMLDivElement
  Args: {
    startDate: Date
    endDate: Date
    startDateDisabled?: boolean
    startDateDisabledText?: string
    themeKey?: ThemeKeys
    onDateSelect?: ({ startDate, endDate }: { startDate: Date; endDate?: Date | null }) => unknown
  }
}

export const AssignmentsAdminDateRange: TOC<Signature> = <template>
  <div data-test-assignment-date-picker class="flex flex-wrap gap-4" ...attributes>
    {{#let (uniqueId) as |uniqId|}}
      <TwoDatePicker @startDate={{@startDate}} @endDate={{@endDate}} @onDateSelect={{@onDateSelect}} as |twoDate|>
        <fieldset class="space-y-1">
          <label for="{{uniqId}}-start" class="block">{{t "components.assignmentsAdmin.dateRange.startDate"}}</label>
          <TwoDatePickerInput
            id="{{uniqId}}-start"
            name="start-date"
            disabled={{@startDateDisabled}}
            @date={{@startDate}}
            @onDateSelect={{twoDate.start.onDateSelect}}
            @min={{twoDate.start.min}}
            @themeKey={{@themeKey}}
          />
          {{#if (and @startDateDisabled @startDateDisabledText)}}
            <Tooltip @text={{@startDateDisabledText}} />
          {{/if}}
        </fieldset>
        <fieldset class="space-y-1">
          <label for="{{uniqId}}-duration-days" class="block">
            {{t "components.assignmentsAdmin.dateRange.duration"}}
          </label>
          <InputText
            id="{{uniqId}}-duration-days"
            name="duration-days"
            class="w-16 text-center"
            type="number"
            value={{twoDate.durationDays}}
            @themeKey={{@themeKey}}
            {{on "input" twoDate.setAbsoluteDays}}
          />
          <span>{{t "components.assignmentsAdmin.dateRange.days"}}</span>
        </fieldset>
        <fieldset class="basis-full space-y-1">
          <label for="{{uniqId}}-end" class="block">{{t "components.assignmentsAdmin.dateRange.endDate"}}</label>
          <TwoDatePickerInput
            id="{{uniqId}}-end"
            name="end-date"
            @date={{@endDate}}
            @onDateSelect={{twoDate.end.onDateSelect}}
            @min={{twoDate.end.min}}
            @max={{twoDate.end.max}}
            @themeKey={{@themeKey}}
          />
        </fieldset>
      </TwoDatePicker>
    {{/let}}
  </div>
</template>

export default AssignmentsAdminDateRange
